import { useState } from 'react';
import { CenterSpinner, EmptyResults } from '@/components/ats';
import { useGetInterviewScorecardsQuery } from '@/store/query/ats-applications';
import { KDrawer } from '@/ui-library/components';
import { Avatar, Flex, useDisclosure } from '@chakra-ui/react';
import {
  ScorecardHeader,
  ViewScorecardDrawer,
} from '@/components/shared/interviews-revamp/scorecard/components';
import { InterviewScorecardForm } from '@/components/shared/interviews-revamp/scorecard/InterviewScorecardForm';
import { useScorecardHelper } from '@/components/shared/interviews-revamp/scorecard/useScorecardFormHelper';
import { InterviewScorecardReadOnly } from '@/components/shared/interviews-revamp/scorecard/InterviewScorecardReadonly';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { getInterviewTimeDetails } from '@/components/shared/interviews-revamp/utils';
import { CsvFailed } from '@/assets/icons/csv-export';
import { OverrideScorecardModal } from '@/components/ats/collect-score-card/OverrideScorecardModal';
import { useShowToast } from '@/hooks';

export const AfterInterviewScorecard = ({ interview }) => {
  const toast = useShowToast();
  const {
    id,
    ats_application_id,
    scorecards: interviewScorecards,
  } = interview || {};

  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
    isUninitialized,
  } = useGetInterviewScorecardsQuery(
    {
      applicationId: ats_application_id,
      params: {
        ats_interview_id: id,
      },
    },
    {
      skip:
        !interview?.id ||
        interviewScorecards?.length === 0 ||
        !ats_application_id,
    }
  );

  const refetchSafe = () => {
    if (!isUninitialized) {
      try {
        refetch();
      } catch {
        console.error('Error refetching scorecards');
        toast({
          title: 'Error refetching scorecards',
          status: 'error',
        });
      }
    }
  };

  const [isSingleScEdit, setIsSingleScEdit] = useState(false);
  const [currScorecard, setCurrScorecard] = useState(null);
  const viewScorecardActions = useDisclosure();
  const submitScorecardActions = useDisclosure();
  const currUser = useSelector(selectCurrUser);
  const { isAfterInterview } = getInterviewTimeDetails(interview);
  const isCandidateNoShowed = interview.meeting_status === 'candidate_no_show';
  const draftScActions = useDisclosure();

  const {
    canEditOthersScorecard,
    scorecards,
    currentScorecard,
    isCurrUserHasScorecard,
    isSomeOtherUserHasDraftScorecardForCurrentScorecard,
  } = useScorecardHelper({
    interview,
    scorecards: data,
  });

  if (isCandidateNoShowed) {
    return (
      <EmptyResults
        icon={CsvFailed}
        wrapperProps={{ h: '100%' }}
        text={'Undo no-show to fill out the scorecard'}
        iconProps={{ h: '140px', w: '142px', mb: '12px' }}
      />
    );
  }

  if (!interviewScorecards?.length) {
    return (
      <EmptyResults
        icon={CsvFailed}
        wrapperProps={{ h: '100%' }}
        text={'No scorecards to fill out in canceled interviews'}
        iconProps={{ h: '140px', w: '142px', mb: '12px' }}
      />
    );
  }

  if (isLoading || isFetching) {
    return <CenterSpinner h="40vh" />;
  }

  // Single SC - Only Currentuser SC and not filled
  const onlyCurrentUserScorecardChecks = () => {
    return (
      isCurrUserHasScorecard &&
      currentScorecard?.status !== 'submitted' &&
      !isSomeOtherUserHasDraftScorecardForCurrentScorecard
    );
  };

  if (onlyCurrentUserScorecardChecks()) {
    return (
      <InterviewScorecardForm
        scorecard={currentScorecard}
        interview={interview}
        onSuccess={() => {
          refetchSafe();
        }}
        scorecardFooterStyles={{
          justifyContent: 'flex-end',
        }}
      />
    );
  }

  // Single SC - Currentuser SC edit or can edit others SC

  const currentUserEditScorecardChecks = () => {
    return (
      canEditOthersScorecard ||
      (isCurrUserHasScorecard &&
        (currentScorecard?.status === 'submitted' ||
          isSomeOtherUserHasDraftScorecardForCurrentScorecard))
    );
  };

  if (currentUserEditScorecardChecks()) {
    const scorecard = scorecards[0];
    const { reviewer } = scorecard || {};
    const { name, avatar } = reviewer || {};
    const isSomeOtherUserHasDraftScorecard =
      scorecard?.status === 'draft' &&
      scorecard?.submitted_by?.id &&
      scorecard?.submitted_by?.id !== currUser?.id;

    if (isSingleScEdit) {
      return (
        <InterviewScorecardForm
          scorecard={scorecard}
          interview={interview}
          onSuccess={() => {
            refetchSafe();
            setIsSingleScEdit(false);
          }}
        />
      );
    }

    return (
      <Flex flexDir="column" h="100%" w="100%" overflow="hidden">
        <ScorecardHeader
          name={name}
          avatar={avatar}
          scorecard={scorecard}
          onSubmitScorecard={() => {
            if (isSomeOtherUserHasDraftScorecard) {
              draftScActions.onOpen();
            } else {
              setIsSingleScEdit(true);
            }
          }}
          skipViewScorecard
          skipShowingFeedback
          refetch={refetchSafe}
          isAfterInterview={isAfterInterview}
        />
        {scorecard?.status === 'submitted' && (
          <Flex flexDir="column" h="100%" w="100%" overflow="auto" px="1rem">
            <InterviewScorecardReadOnly scoreCard={scorecard} />
          </Flex>
        )}
        {draftScActions?.isOpen && (
          <OverrideScorecardModal
            draftScActions={draftScActions}
            onCTAClick={() => {
              setIsSingleScEdit(true);
              draftScActions.onClose();
            }}
            scorecard={scorecard}
          />
        )}
      </Flex>
    );
  }

  // Multiple SC
  return (
    <Flex direction="column" h="100%" w="100%" overflow="scroll">
      {scorecards?.map((scorecard) => {
        const { reviewer } = scorecard || {};
        const { name, avatar } = reviewer || {};
        const isSomeOtherUserHasDraftScorecard =
          scorecard?.status === 'draft' &&
          scorecard?.submitted_by?.id &&
          scorecard?.submitted_by?.id !== currUser?.id;

        return (
          <ScorecardHeader
            key={scorecard?.id}
            name={name}
            avatar={avatar}
            scorecard={scorecard}
            onSubmitScorecard={() => {
              if (isSomeOtherUserHasDraftScorecard) {
                setCurrScorecard(scorecard);
                draftScActions.onOpen();
              } else {
                setCurrScorecard(scorecard);
                submitScorecardActions.onOpen();
              }
            }}
            onViewScorecard={() => {
              setCurrScorecard(scorecard);
              viewScorecardActions.onOpen();
            }}
            refetch={refetchSafe}
            isAfterInterview={isAfterInterview}
          />
        );
      })}
      {/* View Sc Drawer */}
      {currScorecard?.id && viewScorecardActions.isOpen && (
        <ViewScorecardDrawer
          viewScorecardActions={viewScorecardActions}
          currScorecard={currScorecard}
          canEditScorecard={
            currScorecard?.reviewer?.id === currUser?.id ||
            canEditOthersScorecard
          }
          interview={interview}
          refetch={refetchSafe}
        />
      )}
      {/* Submit Sc Drawer */}
      {currScorecard?.id && submitScorecardActions.isOpen && (
        <KDrawer
          isOpen={submitScorecardActions.isOpen}
          onClose={() => {
            refetchSafe();
            submitScorecardActions.onClose();
          }}
          KDrawerHeaderProps={{
            leftItems: (
              <Avatar
                width="20px"
                height="20px"
                borderRadius="xs"
                src={currScorecard?.reviewer?.avatar}
                name={currScorecard?.reviewer?.name}
                className="avatar-sm"
              />
            ),
            title: `${currScorecard?.reviewer?.name}'s Scorecard`,
            titleProps: { color: 'secondary-900' },
          }}
          contentProps={{
            maxW: '650px',
            right: 0,
            position: 'fixed',
            top: 0,
            height: '100vh',
            p: 0,
            borderTopLeftRadius: 'xl',
          }}
          showCloseButton
          skipFooter
        >
          <InterviewScorecardForm
            scorecard={currScorecard}
            interview={interview}
            onSuccess={() => {
              submitScorecardActions.onClose();
              refetchSafe();
            }}
            formContainerStyles={{
              p: '20px 24px',
            }}
          />
        </KDrawer>
      )}
      {draftScActions?.isOpen && (
        <OverrideScorecardModal
          draftScActions={draftScActions}
          onCTAClick={() => {
            draftScActions.onClose();
            submitScorecardActions.onOpen();
          }}
          scorecard={currScorecard}
        />
      )}
    </Flex>
  );
};
